class OrlisseMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.button || !this.panel) return;
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.button.addEventListener('click', () => this.setOpen(!this.hasAttribute('open')), options);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        this.setOpen(false);
        this.button.focus();
      }
    }, options);
    document.addEventListener('click', event => {
      if (this.hasAttribute('open') && !this.contains(event.target)) this.setOpen(false);
    }, options);
    this.panel.addEventListener('click', event => {
      const link = event.target.closest?.('a[href]');
      if (link && this.panel.contains(link)) this.setOpen(false);
    }, options);
    this.addEventListener('focusout', event => {
      // Touching a summary can briefly blur to the document without leaving the menu.
      if (this.isConnected && event.relatedTarget && !this.contains(event.relatedTarget)) this.setOpen(false);
    }, options);
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 990) this.setOpen(false);
    }, options);
  }

  setOpen(open) {
    this.toggleAttribute('open', open);
    this.panel.toggleAttribute('open', open);
    this.button.setAttribute('aria-expanded', String(open));
    this.panel.hidden = !open;
  }

  disconnectedCallback() { this.listeners?.abort(); }
}

class OrlisseProductGallery extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.product__media-list');
    this.thumbnails = Array.from(this.querySelectorAll('.product__media-thumbnails a'));
    if (!this.list || !this.thumbnails.length) return;
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    const paint = () => {
      const left = this.list.getBoundingClientRect().left;
      const media = Array.from(this.list.querySelectorAll('[data-media-id]'));
      const current = media.reduce((nearest, item) =>
        !nearest || Math.abs(item.getBoundingClientRect().left - left) < Math.abs(nearest.getBoundingClientRect().left - left) ? item : nearest, null);
      this.thumbnails.forEach(link => {
        if (link.hash === '#' + current?.id) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    const schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(paint);
    };
    this.list.addEventListener('scroll', schedule, { ...options, passive: true });
    this.thumbnails.forEach(link => link.addEventListener('click', event => {
      const media = document.getElementById(link.hash.slice(1));
      if (!media || !this.list.contains(media)) return;
      event.preventDefault();
      const left = this.list.scrollLeft + media.getBoundingClientRect().left - this.list.getBoundingClientRect().left;
      this.list.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      schedule();
    }, options));
    this.resizeObserver = new ResizeObserver(schedule);
    this.resizeObserver.observe(this.list);
    schedule();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

customElements.define('orlisse-product-gallery', OrlisseProductGallery);

class OrlisseVariantPicker extends HTMLElement {
  connectedCallback() {
    if (document.readyState === 'loading') {
      this.onDocumentReady = () => this.initialize();
      document.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
      return;
    }

    this.initialize();
  }

  disconnectedCallback() {
    if (this.onDocumentReady) {
      document.removeEventListener('DOMContentLoaded', this.onDocumentReady);
    }
  }

  initialize() {
    if (this.initialized) return;
    this.form = document.getElementById(this.dataset.formId);
    this.data = this.querySelector('script[type="application/json"]');
    if (!this.form || !this.data) return;

    try {
      this.variants = JSON.parse(this.data.textContent);
    } catch (error) {
      return;
    }

    this.addEventListener('change', () => this.onVariantChange());
    this.initialized = true;
    this.onVariantChange();
  }

  onVariantChange() {
    const selectedOptions = Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const selectedValue = fieldset.querySelector('[data-selected-value]');
      if (checked && selectedValue) selectedValue.textContent = checked.value;
      return checked ? checked.value : '';
    });

    this.querySelectorAll('fieldset').forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const available = this.variants.some((item) => item.available && item.options.every((value, index) =>
          index === optionIndex ? value === input.value : index < optionIndex ? value === selectedOptions[index] : true
        ));
        input.classList.toggle('is-unavailable', !available);
        const stockLabel = fieldset.querySelector(`[id="${input.getAttribute('aria-describedby')}"]`);
        if (stockLabel) stockLabel.hidden = available;
      });
    });

    const variant = this.variants.find((item) =>
      item.options.every((option, index) => option === selectedOptions[index])
    );

    this.updateVariant(variant);
  }

  updateVariant(variant) {
    const submit = this.form.querySelector('[name="add"]');
    const submitLabel = submit?.querySelector('[data-add-label]') || submit;
    const idInput = this.form.querySelector('[name="id"]');
    const price = document.querySelector('[data-product-price]');
    const compare = document.querySelector('[data-product-compare-price]');
    const unitPrice = document.querySelector('[data-product-unit-price]');
    const sku = document.querySelector('[data-product-sku]');
    const section = this.closest('.product');
    const stock = section?.querySelector('[data-product-stock]');
    const stockText = section?.querySelector('[data-product-stock-text]');
    const saving = section?.querySelector('[data-product-saving]');
    const accelerated = this.form.querySelector('[data-product-accelerated]');
    if (accelerated) accelerated.hidden = !variant?.available;
    if (stock) stock.classList.toggle('is-available', !!variant?.available);
    if (stockText) stockText.textContent = variant?.availability_label || 'Zurzeit nicht verfügbar';
    if (saving) {
      saving.textContent = variant?.saving || '';
      saving.hidden = !variant?.saving;
    }

    if (!variant) {
      if (idInput) idInput.value = '';
      if (submit) {
        submit.disabled = true;
        submitLabel.textContent = submit.dataset.unavailableText;
      }
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (price) price.textContent = variant.price;
    price?.closest('.price')?.classList.toggle('price--on-sale', !!variant.compare_at_price);
    if (compare) {
      compare.textContent = variant.compare_at_price || '';
      compare.hidden = !variant.compare_at_price;
    }
    if (sku) sku.textContent = variant.sku || '';
    if (unitPrice) {
      unitPrice.textContent = variant.unit_price || '';
      unitPrice.hidden = !variant.unit_price;
    }

    if (submit) {
      submit.disabled = !variant.available;
      submitLabel.textContent = variant.available
        ? submit.dataset.addText
        : submit.dataset.soldOutText;
    }

    if (variant.featured_media_id) {
      const media = document.getElementById(`ProductMedia-${variant.featured_media_id}`);
      if (media) {
        const list = media.parentElement;
        list?.querySelectorAll('[data-media-id]').forEach((item) => {
          item.classList.toggle('is-active', item === media);
        });
        if (list?.firstElementChild !== media) list?.prepend(media);
        if (typeof list?.scrollTo === 'function') list.scrollTo({ left: 0, behavior: 'smooth' });

        document.querySelectorAll('.product__media-thumbnails a').forEach((thumbnail) => {
          if (thumbnail.getAttribute('href') === `#ProductMedia-${variant.featured_media_id}`) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
      }
    }

    this.updatePickupAvailability(variant.id);

    if (!this.dataset.updateUrl || this.dataset.updateUrl === 'false') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updatePickupAvailability(variantId) {
    const pickup = document.querySelector('[data-pickup-availability]');
    if (!pickup || !this.dataset.sectionId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', this.dataset.sectionId);
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const replacement = html.querySelector('[data-pickup-availability]');
        if (replacement) pickup.innerHTML = replacement.innerHTML;
      })
      .catch(() => {});
  }
}

class OrlisseRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.dataset.loaded === 'true') return;
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', this.dataset.limit || '4');
    url.searchParams.set('intent', this.dataset.intent || 'related');

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const content = html.querySelector('orlisse-recommendations');
        if (content && content.innerHTML.trim()) {
          this.innerHTML = content.innerHTML;
          this.dataset.loaded = 'true';
          this.dispatchEvent(new CustomEvent('mix:content:loaded', { bubbles: true }));
        } else {
          if (!this.querySelector('.mx-card')) this.remove();
        }
      })
      .catch(() => { if (!this.querySelector('.mx-card')) this.remove(); });
  }
}

class OrlisseQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        const direction = button.dataset.direction === 'increase' ? 1 : -1;
        const current = Number(this.input.value || 1);
        const minimum = Number(this.input.min || 1);
        this.input.value = Math.max(minimum, current + direction);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}

class OrlisseSlideshow extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
    this.dots = Array.from(this.querySelectorAll('[data-slide-to]'));
    this.status = this.querySelector('[data-slide-status]');
    this.pauseButton = this.querySelector('[data-slide-pause]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    if (this.slides.length < 2) return;
    this.querySelector('[data-slide-previous]')?.addEventListener('click', () => this.show(this.index - 1, true), options);
    this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.show(this.index + 1, true), options);
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.slideTo), true), options));
    this.pauseButton?.addEventListener('click', () => this.togglePause(), options);
    this.addEventListener('mouseenter', () => { this.hovered = true; this.stop(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.start(); }, options);
    this.addEventListener('focusin', () => this.stop(), options);
    this.addEventListener('focusout', () => queueMicrotask(() => this.start()), options);
    document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), options);
    document.addEventListener('shopify:block:select', (event) => {
      const selected = this.slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
      if (selected < 0) return;
      this.editorSelected = true;
      this.show(selected);
      this.stop();
    }, options);
    document.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.start(); }, options);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.listeners?.abort();
  }

  show(requestedIndex, userInitiated = false) {
    this.index = (requestedIndex + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => {
      slide.hidden = index !== this.index;
      slide.classList.toggle('is-active', index === this.index);
    });
    this.dots.forEach((dot, index) => dot.setAttribute('aria-pressed', String(index === this.index)));
    if (this.status && userInitiated) {
      this.status.textContent = (this.dataset.statusTemplate || 'Slide {{ number }} of {{ total }}')
        .replace('{{ number }}', String(this.index + 1)).replace('{{ total }}', String(this.slides.length));
    }
    if (userInitiated) { this.stop(); this.start(); }
  }

  start() {
    this.stop();
    if (!this.isConnected || this.dataset.autoplay !== 'true' || this.paused || this.hovered ||
        this.editorSelected || document.hidden || this.contains(document.activeElement) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), Number(this.dataset.interval || 7000));
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  togglePause() {
    this.paused = !this.paused;
    this.pauseButton.setAttribute('aria-pressed', String(this.paused));
    this.pauseButton.textContent = this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
    if (this.paused) this.stop(); else this.start();
  }
}

class OrlisseCarousel extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.track = this.querySelector('[data-carousel-track]');
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    if (!this.track || !this.previousButton || !this.nextButton) return;
    this.previousButton.addEventListener('click', () => this.scroll(-1), options);
    this.nextButton.addEventListener('click', () => this.scroll(1), options);
    this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
    this.updateControls();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.resizeObserver?.disconnect();
  }

  scroll(direction) {
    const card = this.track.querySelector('.product-card, .mega-product-card');
    const style = getComputedStyle(this.track);
    const gap = Number.parseFloat(style.columnGap || 0);
    const distance = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * .8;
    this.track.scrollBy({
      left: distance * direction * (style.direction === 'rtl' ? -1 : 1),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
    });
  }

  updateControls() {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth - 2);
    const position = Math.abs(this.track.scrollLeft);
    this.previousButton.disabled = position <= 2;
    this.nextButton.disabled = position >= maxScroll;
  }
}

class OrlissePredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;
    this.timer = null;
    this.abortController = null;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 180);
    });
    this.input.addEventListener('focus', () => {
      if (this.results.innerHTML.trim()) this.open();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.results.innerHTML = '';
      this.close();
      return;
    }
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const url = new URL(`${window.Shopify?.routes?.root || '/'}search/suggest` , window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('section_id', 'predictive-search');
    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.ok ? response.text() : Promise.reject(new Error('Predictive search failed')))
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const content = html.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = content ? content.innerHTML : '';
        if (this.results.innerHTML.trim()) this.open(); else this.close();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') this.close();
      });
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

class OrlisseCartDrawer extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.panel = this.querySelector('[data-cart-drawer-panel]');
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.status = this.querySelector('[data-cart-drawer-status]');
    this.addedLabel = this.querySelector('[data-cart-drawer-added]');
    if (!this.panel || !this.overlay) return;

    this.abortController = new AbortController();
    const listenerOptions = { signal: this.abortController.signal };
    this.querySelectorAll('[data-cart-drawer-close]').forEach((control) => control.addEventListener('click', () => this.close(), listenerOptions));
    this.querySelectorAll('[data-cart-quantity]').forEach((control) => control.addEventListener('click', () => this.change(control.dataset.lineKey, Number(control.dataset.quantity)), listenerOptions));
    this.querySelectorAll('[data-cart-remove]').forEach((control) => control.addEventListener('click', () => this.change(control.dataset.lineKey, 0), listenerOptions));
    this.querySelectorAll('[data-cart-quantity-input]').forEach((input) => input.addEventListener('change', () => this.change(input.dataset.lineKey, Number(input.value)), listenerOptions));

    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.clearFeedback();
      this.open(trigger);
    }, listenerOptions));

    document.addEventListener('submit', (event) => this.handleProductSubmit(event), listenerOptions);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
      if (event.key === 'Tab' && this.hasAttribute('open')) {
        const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex="0"]')].filter(el => el.getClientRects().length && !el.closest('[hidden]'));
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    }, listenerOptions);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.feedbackTimeout);
  }

  open(trigger = null) {
    if (this.hasAttribute('open')) return;
    this.previousFocus = trigger || document.activeElement;
    this.overlay.hidden = false;
    this.panel.hidden = false;
    this.panel.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((item) => item.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('cart-drawer-open');
    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      this.querySelector('[data-cart-drawer-close]')?.focus();
    });
  }

  close() {
    this.clearFeedback();
    this.removeAttribute('open');
    this.panel.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('cart-drawer-open');
    window.setTimeout(() => {
      if (!this.hasAttribute('open')) {
        this.overlay.hidden = true;
        this.panel.hidden = true;
      }
    }, 260);
    if (this.previousFocus instanceof HTMLElement) this.previousFocus.focus();
  }

  async handleProductSubmit(event) {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
    if (form.dataset.mxCartSubmitting === 'true') { event.preventDefault(); return; }
    if (event.submitter?.classList.contains('shopify-payment-button__button')) return;
    event.preventDefault();

    form.dataset.mxCartSubmitting = 'true';
    const submit = event.submitter || form.querySelector('[name="add"]');
    if (submit) submit.disabled = true;
    this.setAttribute('loading', '');

    try {
      await this.withCartLock(async () => {
      const response = await fetch(`${this.rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || this.dataset.cartError);
      await this.refresh(true, this.dataset.cartAdded, true);
      });
    } catch (error) {
      this.showStatus(error instanceof SyntaxError ? this.dataset.cartError : error.message || this.dataset.cartError, true);
      this.open(submit);
    } finally {
      delete form.dataset.mxCartSubmitting;
      this.removeAttribute('loading');
      if (submit) submit.disabled = false;
    }
  }

  async change(lineKey, quantity) {
    if (!lineKey || !Number.isFinite(quantity) || this.hasAttribute('loading')) return;
    this.setAttribute('loading', '');
    try {
      await this.withCartLock(async () => {
      // Discounts can change line keys after the drawer was rendered.
      const currentResponse = await fetch(`${this.rootPath()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!currentResponse.ok) throw new Error(this.dataset.cartError);
      const currentCart = await currentResponse.json();
      let item = currentCart.items.find(line => line.key === lineKey);
      if (!item) {
        const variants = currentCart.items.filter(line => String(line.variant_id) === lineKey.split(':')[0]);
        if (variants.length === 1) item = variants[0];
      }
      if (!item) { await this.refresh(true, this.dataset.cartUpdated); return; }
      const response = await fetch(`${this.rootPath()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: Math.max(0, quantity) })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || this.dataset.cartError);
      this.updateCartCount(cart.item_count);
      await this.refresh(true, this.dataset.cartUpdated);
      });
    } catch (error) {
      this.showStatus(error instanceof SyntaxError ? this.dataset.cartError : error.message || this.dataset.cartError, true);
    } finally {
      this.removeAttribute('loading');
    }
  }

  async withCartLock(task) {
    // PromoEasy uses the same lock to reconcile its free gift. Serializing cart
    // writes prevents a gift mutation racing the shopper's quantity update.
    if (navigator.locks?.request) return navigator.locks.request('promo-easy-cart', task);
    return task();
  }

  async refresh(shouldOpen, message = '', wasAdded = false) {
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(`${this.rootPath()}?section_id=${encodeURIComponent(this.dataset.sectionId)}`),
      fetch(`${this.rootPath()}cart.js`, { headers: { Accept: 'application/json' } })
    ]);
    if (!sectionResponse.ok || !cartResponse.ok) throw new Error(this.dataset.cartError);
    const [sectionText, cart] = await Promise.all([sectionResponse.text(), cartResponse.json()]);
    const parsed = new DOMParser().parseFromString(sectionText, 'text/html');
    const replacement = parsed.querySelector('orlisse-cart-drawer');
    if (!replacement) throw new Error(this.dataset.cartError);
    this.updateCartCount(cart.item_count);
    const wasOpen = this.hasAttribute('open');
    const previousFocus = this.previousFocus;
    const focusedControl = document.activeElement;
    const focusedKey = focusedControl?.dataset?.lineKey;
    if (wasOpen && shouldOpen) {
      replacement.setAttribute('open', '');
      replacement.querySelector('.cart-drawer__overlay').hidden = false;
      const panel = replacement.querySelector('[data-cart-drawer-panel]');
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
    }
    this.replaceWith(replacement);
    replacement.previousFocus = previousFocus;
    if (shouldOpen) {
      if (!wasOpen) replacement.open(previousFocus);
      else if (focusedKey) {
        const control = [...replacement.querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === focusedKey && el.tagName === focusedControl.tagName && (el.dataset.quantity || '') === (focusedControl.dataset.quantity || ''));
        (control || replacement.querySelector('[data-cart-drawer-close]'))?.focus({ preventScroll: true });
      }
      if (message) replacement.showStatus(message, false, wasAdded);
    }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = String(count || 0);
      counter.hidden = !count;
    });
  }

  clearFeedback() {
    window.clearTimeout(this.feedbackTimeout);
    if (this.addedLabel) this.addedLabel.hidden = true;
    if (this.status) {
      this.status.textContent = '';
      this.status.classList.remove('cart-drawer__status--error');
    }
  }

  showStatus(message, isError = false, wasAdded = false) {
    this.clearFeedback();
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('cart-drawer__status--error', isError);
    if (this.addedLabel) this.addedLabel.hidden = !wasAdded || isError;
    // Success feedback describes the last action, not the current cart contents.
    if (message && !isError) this.feedbackTimeout = window.setTimeout(() => this.clearFeedback(), 5000);
  }

  rootPath() {
    return window.Shopify?.routes?.root || '/';
  }
}

customElements.define('orlisse-menu', OrlisseMenu);
customElements.define('orlisse-variant-picker', OrlisseVariantPicker);
customElements.define('orlisse-recommendations', OrlisseRecommendations);
customElements.define('orlisse-quantity', OrlisseQuantity);
customElements.define('orlisse-slideshow', OrlisseSlideshow);
customElements.define('orlisse-carousel', OrlisseCarousel);
customElements.define('orlisse-predictive-search', OrlissePredictiveSearch);
customElements.define('orlisse-cart-drawer', OrlisseCartDrawer);
